import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { GetMyPermissionsUseCase } from "../../domain/use-cases/GetMyPermissionsUseCase";
import { PermissionRepositoryImpl } from "../../data/repositories/PermissionRepositoryImpl";
import { PermissionsMap } from "../../domain/entities/PermissionEntity";
import { ModuleCode, ActionCode } from "../../constants/permissions";
import { useAuth } from "../../../features/auth/presentation/hooks/useAuth";

// Create repository instance (singleton)
const permissionRepository = new PermissionRepositoryImpl();

// Create use case
const getMyPermissionsUseCase = new GetMyPermissionsUseCase(
  permissionRepository,
);

interface PermissionsContextType {
  permissions: PermissionsMap | null;
  loading: boolean;
  error: Error | null;
  hasPermission: (
    module: ModuleCode | string,
    action: ActionCode | string,
  ) => boolean;
  requiresReview: (
    module: ModuleCode | string,
    action: ActionCode | string,
  ) => boolean;
  getPermissionInfo: (
    module: ModuleCode | string,
    action: ActionCode | string,
  ) => { granted: boolean; requiresReview: boolean };
  refreshPermissions: () => Promise<void>;
}

export const PermissionsContext = createContext<
  PermissionsContextType | undefined
>(undefined);

interface PermissionsProviderProps {
  children: React.ReactNode;
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({
  children,
}) => {
  const [permissions, setPermissions] = useState<PermissionsMap | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const fetchingRef = useRef<boolean>(false); // Prevent concurrent fetches
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const previousUserIdRef = useRef<string | null>(null);

  const fetchPermissions = useCallback(async () => {
    // Prevent multiple simultaneous fetches
    if (fetchingRef.current) {
      return;
    }

    try {
      fetchingRef.current = true;
      setLoading(true);
      setError(null);
      const perms = await getMyPermissionsUseCase.execute();
      setPermissions(perms);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to fetch permissions"),
      );
      console.error("Error fetching permissions:", err);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  // Fetch permissions on mount and when user changes (login/logout/switch user)
  useEffect(() => {
    // Wait for auth to finish loading before fetching permissions
    if (authLoading) {
      return;
    }

    const currentUserId = user?.id || null;
    const previousUserId = previousUserIdRef.current;

    // If user ID changed (different user logged in) or user logged in/out, refresh permissions
    // OR if this is the initial mount and user is authenticated (previousUserId is null)
    if (
      currentUserId !== previousUserId ||
      (previousUserId === null && isAuthenticated && user)
    ) {
      // Update the ref for next comparison
      previousUserIdRef.current = currentUserId;

      // Only fetch if authenticated (user exists)
      if (isAuthenticated && user) {
        fetchPermissions();
      } else if (!isAuthenticated) {
        // Clear permissions when user logs out
        setPermissions(null);
        setLoading(false);
      }
    }
  }, [user, isAuthenticated, authLoading, fetchPermissions]);

  /**
   * Check if user has permission for a specific module-action
   */
  const hasPermission = useCallback(
    (module: ModuleCode | string, action: ActionCode | string): boolean => {
      if (!permissions) {
        return false;
      }

      return permissions[module]?.[action]?.granted ?? false;
    },
    [permissions],
  );

  /**
   * Check if action requires review for user
   */
  const requiresReview = useCallback(
    (module: ModuleCode | string, action: ActionCode | string): boolean => {
      if (!permissions) {
        return false;
      }

      return permissions[module]?.[action]?.requires_review ?? false;
    },
    [permissions],
  );

  /**
   * Get permission info (granted and requires_review) for a module-action
   * More efficient than calling hasPermission() and requiresReview() separately
   */
  const getPermissionInfo = useCallback(
    (
      module: ModuleCode | string,
      action: ActionCode | string,
    ): { granted: boolean; requiresReview: boolean } => {
      if (!permissions) {
        return { granted: false, requiresReview: false };
      }

      const perm = permissions[module]?.[action];
      const granted = perm?.granted ?? false;
      const requiresReview = granted && (perm?.requires_review ?? false);

      return { granted, requiresReview };
    },
    [permissions],
  );

  /**
   * Refresh permissions manually if needed
   */
  const refreshPermissions = useCallback(async () => {
    await fetchPermissions();
  }, [fetchPermissions]);

  // Combine auth loading and permissions loading states
  const isLoading = authLoading || loading;

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        hasPermission,
        requiresReview,
        getPermissionInfo,
        loading: isLoading,
        error,
        refreshPermissions,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};
